import { faArrowRightToBracket, faAward, faBookmark, faBriefcase, faCartShopping, faCircleUser, faDoorClosed, faGear, faKey, faMemoPad, faMessageSmile, faMoon, faNewspaper, faRocket, faSchool, faSquareA, faSuitcase } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { AdminLink } from '@tryhackme/thm-ui-components';
import type { AvatarDropdownCategoriesProps } from '@tryhackme/thm-ui-components/dist/components/compounds/header/components/avatar-dropdown/avatar-dropdown.types';
import { UserAccountTypes, type UserRoles } from 'src/common/enums';
import { useFeatureIsOn } from 'src/common/growthbook';
import type { AdminLinkWithId } from '../header.types';
import { conditionalLink } from '../use-main-navigation-links';
import { adminLinks, linksByRole } from './admin-links';

// Keep in mind to replace dynamic content that is in brackets e.g. [username]
// If you are changing this, change this property in component library to 3, because we are using indexes there
export const avatarDropdownItems = (username: string | undefined, urgencyIndicator: boolean): AvatarDropdownCategoriesProps => {
  const isNewNavEnabled = useFeatureIsOn('new-navigation');
  const allItems = [{
    items: [{
      name: 'View Profile',
      href: `/p/${username ?? ''}`,
      icon: <FontAwesomeIcon icon={faCircleUser} />
    }, {
      name: 'Manage Account',
      href: '/manage-account',
      icon: <FontAwesomeIcon icon={faGear} />,
      urgencyIndicator
    }]
  }, {
    items: [...conditionalLink(isNewNavEnabled, {
      name: 'For Business',
      href: 'https://business.tryhackme.com',
      icon: <FontAwesomeIcon icon={faBriefcase} />,
      isLinkOpenedInNewTab: true
    }), ...conditionalLink(isNewNavEnabled, {
      name: 'For Education',
      href: '/classrooms',
      icon: <FontAwesomeIcon icon={faSchool} />,
      isLinkOpenedInNewTab: true
    })]
  }, {
    items: [...conditionalLink(isNewNavEnabled, {
      name: 'Saved',
      href: '/rooms',
      icon: <FontAwesomeIcon icon={faBookmark} />
    }), ...conditionalLink(isNewNavEnabled, {
      name: 'Badges',
      href: '/badges',
      icon: <FontAwesomeIcon icon={faAward} />
    })]
  }, {
    items: [...conditionalLink(isNewNavEnabled, {
      name: 'Resources',
      icon: <FontAwesomeIcon icon={faMemoPad} />,
      subMenuItems: [{
        name: 'Glossary',
        href: '/glossary',
        icon: <FontAwesomeIcon icon={faSquareA} />
      }, {
        name: 'Blog',
        href: '/resources/blog',
        icon: <FontAwesomeIcon icon={faNewspaper} />
      }, {
        name: 'Career Hub',
        href: '/careers',
        icon: <FontAwesomeIcon icon={faSuitcase} />
      }]
    })]
  }, {
    items: [...conditionalLink(!isNewNavEnabled, {
      name: 'Badges',
      href: '/badges',
      icon: <FontAwesomeIcon icon={faAward} />
    }), ...conditionalLink(!isNewNavEnabled, {
      name: 'My Rooms',
      href: '/rooms',
      icon: <FontAwesomeIcon icon={faDoorClosed} />
    }), ...conditionalLink(!isNewNavEnabled, {
      name: 'Access',
      href: '/access',
      icon: <FontAwesomeIcon icon={faKey} />
    }), ...conditionalLink(!isNewNavEnabled, {
      name: 'Give Feedback',
      href: '/feedback',
      icon: <FontAwesomeIcon icon={faMessageSmile} />
    })]
  }, {
    items: [...conditionalLink(isNewNavEnabled, {
      name: 'Swag Shop',
      href: '/swag-shop',
      icon: <FontAwesomeIcon icon={faCartShopping} />,
      isLinkOpenedInNewTab: true
    }), ...conditionalLink(isNewNavEnabled, {
      name: 'Buy Vouchers',
      href: '/subscriptions',
      icon: <FontAwesomeIcon icon={faRocket} />
    }), ...conditionalLink(isNewNavEnabled, {
      name: 'Give Feedback',
      href: '/feedback',
      icon: <FontAwesomeIcon icon={faMessageSmile} />
    })]
  }, {
    items: [{
      name: 'Log Out',
      href: '/logout',
      icon: <FontAwesomeIcon icon={faArrowRightToBracket} />
    }]
  }];
  const darkModeObject = {
    id: 'dark-mode',
    name: 'Dark Mode',
    icon: <FontAwesomeIcon icon={faMoon} />,
    href: '',
    isLinkOpenedInNewTab: false,
    urgencyIndicator: false,
    subMenuItems: []
  };
  allItems[0]?.items.push(darkModeObject);
  return allItems;
};
const sortLinksById = (a: AdminLinkWithId, b: AdminLinkWithId) => a.id - b.id;
export const getAdminLinks = ({
  roles,
  accountType
}: {
  roles?: UserRoles[];
  accountType?: UserAccountTypes;
} = {}) => {
  const links: (AdminLink & {
    id: number;
  })[] = [];

  // If user is an admin, return all links except submissions
  if (accountType === UserAccountTypes.ADMIN) {
    links.push(...adminLinks);
  }
  if (!roles?.length) return links.sort(sortLinksById) as AdminLink[];

  // If user is not an admin or has is an admin but has a role with a link that admins cannot see, add links based on the roles
  for (const role of roles) {
    const currentLinks = linksByRole[role];
    // If there are links for the current role
    if (currentLinks) {
      for (const currentLink of currentLinks) {
        if (!links.some(link => currentLink.title === link.title)) links.push(currentLink);
      }
    }
  }
  return links.sort(sortLinksById) as AdminLink[];
};
export const IS_BIG_STREAK_MILESTONE_DISMISSED = 'isBigStreakMilestoneDismissed';